import { Close, Mail } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import dayjs from "dayjs";
import React from "react";
import Modal from "../../App/Components/Modal/Modal";
import { useGetClientTextTemplatesQuery } from "../../App/Queries/MessageQueries";
import { useMessageInputHandlers } from "../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageTemplateHandlers,
  useMessageTemplateState,
} from "../../App/State/Hooks/MessageDialog/useMessageTemplate";
import { IMessageHistory, ITextTemplate } from "../../App/Typings/MessageText";
import { ITableColumn } from "../../App/Typings/TableColumn";
import { MessageHistoryListItem } from "../MessageDialog/Components/MessageHistory";
import MessageTemplateTableActions from "./Components/MessageTemplateTableActions";
import { truncateMessageBody } from "./EmailTemplateListDialog";
import { MessageTemplateTable } from "./MessageTemplateTable";

const columnsNoActionButtons: ITableColumn[] = [
  { label: "Created", propName: "dateDisplay", order: 0 },
  { label: "Name", propName: "name", order: 1 },
  { label: "Body", propName: "body", order: 2 },
  { label: "Visibility", propName: "visibility", order: 3 },
  { label: "Actions", propName: "actions", order: 4 },
];

export default function TextTemplateListDialog({
  buttonName,
  handleSelected,
  handleShowTextTemplateClick,
  handleCloseTextTemplateDialog,
  showDialog,
  ButtonProps,
}: {
  buttonName: string;
  handleSelected?: (row: any, select: boolean, e: any) => void;
  handleShowTextTemplateClick?: () => void;
  handleCloseTextTemplateDialog?: () => void;
  showDialog?: boolean;
  ButtonProps?: ButtonProps;
}) {
  const { handleApplyTextTemplate } = useMessageInputHandlers();
  const messageTemplateState = useMessageTemplateState();
  const { handleShowTemplateDialog, handleHideTemplateDialog, handleTemplateSelected } =
    useMessageTemplateHandlers();
  const { data: textTemplates, isLoading } = useGetClientTextTemplatesQuery();

  const [showPreview, setShowPreview] = React.useState(false);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = React.useState<ITextTemplate | null>(null);
  const [previewTemplateView, setPreviewTemplateView] = React.useState<IMessageHistory | null>(null);

  const truncatedTableData =
    textTemplates?.map((template): ITextTemplate => {
      return {
        ...template,
        body: truncateMessageBody(template.body),
      };
    }) || [];

  const handleSelectedRow = (row: ITextTemplate, select: boolean, e: any) => {
    const originalRow = textTemplates?.find((template) => template.textTemplateId === row.textTemplateId);
    if (originalRow) {
      handleSelected
        ? handleSelected(originalRow, select, e)
        : handleTemplateSelected(originalRow, handleApplyTextTemplate);
    }
  };

  const handlePreview = (row: ITextTemplate) => {
    const originalRow = textTemplates?.find((template) => template.textTemplateId === row.textTemplateId);
    if (!originalRow) {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
      setShowPreview(false);
      return;
    }
    setPreviewTemplateView({
      messageId: "",
      messageTrackingId: "",
      scheduledTimeEastern: dayjs().utc().format("YYYY-MM-DDTHH:mm:ss"),
      messageDisplay: originalRow.body,
      messageState: "DELIVERED",
      userName: "",
      created: dayjs().utc(),
      candidateCount: 1,
      type: "DirectTextMessage",
      scheduledUtcTime: dayjs().utc(),
      dateSort: dayjs().utc(),
      messageType: "DirectTextMessage",
      isScheduled: false,
      groupName: "",
      errorMessage: "",
      hasBeenRead: false,
      userHasReplyNotification: false,
    });
    setSelectedPreviewTemplate(originalRow);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setTimeout(() => {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
    }, 300);
  };

  const buttonTitle = isLoading
    ? "Loading..."
    : textTemplates?.length === 0
      ? "No Text Templates"
      : "Show Text Templates";

  return (
    <Box>
      <Button
        startIcon={<Mail />}
        id="load-text-templates"
        title={buttonTitle}
        {...ButtonProps}
        aria-haspopup="true"
        disableElevation
        disabled={isLoading || !textTemplates || textTemplates.length === 0}
        onClick={handleShowTextTemplateClick || handleShowTemplateDialog}
      >
        {buttonName}
      </Button>
      <Dialog
        id="text-template-dialog"
        aria-labelledby="text-template-dialog"
        open={showDialog || messageTemplateState.showTemplateDialog}
        onClose={handleCloseTextTemplateDialog || handleHideTemplateDialog}
        scroll="paper"
        fullWidth={true}
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxHeight: { xs: "calc(100vh - 1rem)", sm: "calc(100vh - 64px)" },
            height: { xs: "100vh", sm: "90vh" },
            width: { xs: "calc(100% - 1rem)", sm: "calc(100% - 2rem)" },
            m: { xs: "1rem", sm: "2rem" },
          },
        }}
      >
        <DialogTitle
          id="text-template-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            color: "white",
            backgroundColor: "#00af4a",
          }}
        >
          <Box>Text Templates</Box>
          <Box>
            <IconButton
              onClick={handleCloseTextTemplateDialog || handleHideTemplateDialog}
              aria-label="Close Text Templates Window"
              aria-controls="text-template-window"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers={true}
          sx={{
            display: "grid",
            gridTemplateColumns: "100%",
            gridTemplateRows: "auto 1fr",
            height: "100%",
          }}
        >
          <MessageTemplateTable
            name="text"
            columns={columnsNoActionButtons}
            loading={isLoading}
            tableData={truncatedTableData.map((c) => {
              return {
                ...c,
                actions: (
                  <MessageTemplateTableActions
                    id={c.textTemplateId}
                    onPreviewClick={() => handlePreview(c)}
                  />
                ),
              };
            })}
            handleSelected={handleSelectedRow}
          />
          <Modal
            title={`Preview Text Template: ${selectedPreviewTemplate ? selectedPreviewTemplate.name : ""}`}
            isOpen={showPreview}
            handleCloseModal={handleClosePreview}
            dataTestId={`preview-template${
              selectedPreviewTemplate ? "-" + selectedPreviewTemplate.textTemplateId : ""
            }`}
            content={
              previewTemplateView ? (
                <MessageHistoryListItem msg={previewTemplateView} showDate={false} />
              ) : (
                <></>
              )
            }
            footerContent={<></>}
            size="sm"
          />
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <ButtonGroup>
            <Button
              style={{ marginTop: "20px" }}
              variant={"outlined"}
              color="primary"
              onClick={handleCloseTextTemplateDialog || handleHideTemplateDialog}
            >
              Close
            </Button>
          </ButtonGroup>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export const MemoTextTemplateListDialog = React.memo(TextTemplateListDialog);
